import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent } from '@/app/components/ui/card';
import { toast } from '@/app/components/ui/simple-toaster';
import { handleApiError } from '@/lib/api-client';
import { InspectionForm } from '@/components/inspections/inspection-form';
import { useCreateInspection } from '@/hooks/use-inspections';
import { useAsset, useAssets } from '@/hooks/use-assets';
import { PageBreadcrumb } from '@/app/components/page-breadcrumb';
import type { Asset, CreateInspectionPayload } from '@/lib/types';

export function CreateInspectionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [assetSearch, setAssetSearch] = useState('');

  const preselectedAssetIdParam = searchParams.get('asset');
  const preselectedAssetId = preselectedAssetIdParam ? parseInt(preselectedAssetIdParam, 10) : 0;

  const { data: assetsData, isLoading: assetsLoading } = useAssets({
    page_size: 50,
    q: assetSearch,
  });

  const { data: preselectedAsset } = useAsset(preselectedAssetId);

  const createMutation = useCreateInspection();

  const handleSubmit = async (data: CreateInspectionPayload) => {
    try {
      const inspection = await createMutation.mutateAsync(data);
      toast.success('Inspection created successfully');
      navigate(`/inspections/${inspection.id}`);
    } catch (error) {
      const apiError = handleApiError(error);
      toast.error(
        apiError.status === 403
          ? 'You are not Inspection Certified, so you cannot sign off inspections. ' +
            'An admin can enable Inspection Certified on your tech profile.'
          : apiError.message || 'Failed to create inspection. Please try again.'
      );
    }
  };

  const handleSearchAssets = (query: string) => {
    setAssetSearch(query);
  };

  const baseAssets = assetsData?.results || [];
  const assets: Asset[] = preselectedAsset && !baseAssets.find((a) => a.id === preselectedAsset.id)
    ? [preselectedAsset, ...baseAssets]
    : baseAssets;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageBreadcrumb items={[
        { label: 'Inspections', href: '/inspections' },
        { label: 'Create Inspection' },
      ]} />
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/inspections')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Create Inspection
          </h1>
          <p className="text-gray-600 mt-1">
            Record a new equipment inspection
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          <InspectionForm
            assets={assets}
            assetsLoading={assetsLoading}
            preselectedAssetId={preselectedAssetId || undefined}
            onSearchAssets={handleSearchAssets}
            onSubmit={handleSubmit}
            isSubmitting={createMutation.isPending}
            onCancel={() => navigate('/inspections')}
          />
        </CardContent>
      </Card>
    </div>
  );
}
